import { useEffect, useState } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";

import type { NamedResourceAdapter } from "./types";

export type DeleteNamedResourceDialogProps<TItem> = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  item: TItem | null;
  adapter: NamedResourceAdapter<TItem>;
};

export function DeleteNamedResourceDialog<TItem>({
  open,
  onOpenChange,
  item,
  adapter,
}: DeleteNamedResourceDialogProps<TItem>) {
  const deleteMutation = adapter.useDelete();
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSubmitError(null);
    }
  }, [open]);

  async function onConfirm() {
    if (item === null) {
      return;
    }
    setSubmitError(null);
    try {
      await deleteMutation.mutateAsync({ id: adapter.getId(item) });
      onOpenChange(false);
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : `Failed to delete ${adapter.singularLabel}. Try again.`,
      );
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Delete this {adapter.singularLabel}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {item === null
              ? null
              : `${adapter.getName(item)} will be hidden from the active list. You can restore it from the Show deleted view.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        {submitError !== null && (
          <p role="alert" className="text-sm font-medium text-error">
            {submitError}
          </p>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={deleteMutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className="bg-danger text-on-danger hover:opacity-90"
            disabled={deleteMutation.isPending || item === null}
            onClick={(event) => {
              event.preventDefault();
              void onConfirm();
            }}
          >
            {deleteMutation.isPending ? "Deleting…" : "Delete"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
